/* =============================================================================
 * CoachContactModal — one entry point for contacting a coach.
 *
 * Replaces three separate, deep, modal-over-modal composers (Email / Tweet / DM)
 * that were each opened from their own button and each re-ran the same
 * coach-context + connection scaffolding. This shell owns ONE overlay + a
 * channel selector; the active channel renders its existing composer in
 * `embedded` mode (Modal's `inline` prop), so all the tested compose/validate/
 * send logic is reused untouched — the athlete just switches channels with a
 * tab instead of closing one modal to open another.
 *
 * Per the audit: Email + Tweet are "write text + send" forms and Tweet's DM is a
 * live chat thread — kept as its own channel, not folded into a compose form.
 * ========================================================================== */
import React, { useEffect, useState } from 'react';
import { Mail, MessageCircle } from 'react-feather';
import { XIcon } from './XIcon';
import Modal from './Modal';
import EmailCoachModal from './EmailCoachModal';
import TweetCoachModal from './TweetCoachModal';
import DMCoachModal from './DMCoachModal';
import { useResponsive } from '../hooks/useResponsive';

export type ContactChannel = 'email' | 'tweet' | 'dm';

export interface ContactCoach {
  name: string;
  email?: string;
  title?: string;
  school?: string;
  twitter_handle?: string;
  coach_id?: string;
  school_id?: string;
}

interface CoachContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  coach: ContactCoach | null;
  /** The channel the athlete clicked into. */
  initialChannel: ContactChannel;
}

const CHANNEL_META: Record<ContactChannel, { label: string; icon: React.ReactNode }> = {
  email: { label: 'Email', icon: <Mail size={16} aria-hidden /> },
  tweet: { label: 'Tweet', icon: <XIcon size={16} aria-hidden /> },
  dm: { label: 'Direct message', icon: <MessageCircle size={16} aria-hidden /> },
};

const CoachContactModal: React.FC<CoachContactModalProps> = ({ isOpen, onClose, coach, initialChannel }) => {
  const { isMobile } = useResponsive();
  const [activeChannel, setActiveChannel] = useState<ContactChannel>(initialChannel);

  // Land on whatever channel the athlete clicked each time the shell opens.
  useEffect(() => {
    if (isOpen) setActiveChannel(initialChannel);
  }, [isOpen, initialChannel]);

  if (!coach) return null;

  // Only offer channels this coach actually has contact info for. (Tweet + DM
  // both need the X handle, which the caller reveals via the §1.5 unlock before
  // opening; email needs an address.)
  const hasEmail = !!coach.email;
  const hasX = !!coach.twitter_handle;
  const channels: ContactChannel[] = [
    ...(hasEmail ? (['email'] as const) : []),
    ...(hasX ? (['tweet', 'dm'] as const) : []),
  ];

  // If the requested channel isn't available (shouldn't happen — the caller
  // opens the channel it has data for), fall back to the first available one.
  const current: ContactChannel = channels.includes(activeChannel)
    ? activeChannel
    : (channels[0] ?? 'email');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={`Contact ${coach.name}`}
      zIndex={1001}
      // Close only through the active composer's own X / Cancel, which route
      // through its dirty-guard ("Discard changes?"). A backdrop/Escape click on
      // the shell would bypass that guard and silently drop a composed message.
      disableClose
      contentStyle={{
        background: 'white',
        borderRadius: '16px',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '700px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      {/* Channel selector — one destination, one selector. Only rendered when the
          coach supports more than one channel; otherwise the composer stands
          alone (no pointless single-tab bar). */}
      {channels.length > 1 && (
        <div
          role="tablist"
          aria-label="How to contact this coach"
          style={{
            display: 'flex',
            gap: '6px',
            padding: '12px 16px',
            borderBottom: '1px solid #E5E7EB',
            flexShrink: 0,
          }}
        >
          {channels.map((ch) => {
            const active = ch === current;
            return (
              <button
                key={ch}
                role="tab"
                aria-selected={active}
                onClick={() => { if (!active) setActiveChannel(ch); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: active ? 'default' : 'pointer',
                  background: active ? '#F9550D' : 'transparent',
                  color: active ? 'white' : '#6B7280',
                  fontWeight: 700,
                  fontSize: '13px',
                  fontFamily: 'Manrope, sans-serif',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
              >
                {CHANNEL_META[ch].icon}
                {CHANNEL_META[ch].label}
              </button>
            );
          })}
        </div>
      )}

      {/* Active channel — its existing composer, embedded (own header/body/footer,
          no own overlay). Only the active one is mounted. */}
      {current === 'email' && (
        <EmailCoachModal
          embedded
          isOpen
          onClose={onClose}
          coach={{
            name: coach.name,
            email: coach.email || '',
            title: coach.title,
            school: coach.school,
            coach_id: coach.coach_id,
            school_id: coach.school_id,
          }}
        />
      )}

      {current === 'tweet' && (
        <TweetCoachModal
          embedded
          isOpen
          onClose={onClose}
          coachName={coach.name}
          twitterHandle={coach.twitter_handle || ''}
          coachId={coach.coach_id}
          schoolId={coach.school_id}
        />
      )}

      {current === 'dm' && (
        <DMCoachModal
          embedded
          isOpen
          closeOnSend
          onClose={onClose}
          coach={{
            name: coach.name,
            twitter_handle: coach.twitter_handle || '',
            coach_id: coach.coach_id,
            school_id: coach.school_id,
          }}
        />
      )}
    </Modal>
  );
};

export default CoachContactModal;
